/** The guided "Create an agent" flow (ADR-0058). Plain words for business users. */
export default {
  en: {
    page: {
      subtitle: "Create and release agents in plain steps, or open the advanced editors.",
      wizardTitle: "Create an agent",
      wizardSubtitle: "One question per step. The summary on the side shows what your agent will do.",
      home: "All agents",
      advanced: "Advanced editor",
      guided: "Create an agent (guided)",
    },
    home: {
      heroTitle: "Create an agent in six short steps",
      heroBody: "Answer a few questions in plain words. We build the agent, check it against the platform rules and help you release it.",
      start: "Create an agent",
      templatesTitle: "Start from a ready example",
      templatesHint: "An example fills in the answers for you. You can change anything before you save.",
      useTemplate: "Use this example",
      useTemplateAria: "Use the example {{name}}",
      agentsTitle: "Your agents",
      agentsFailed: "Your agents could not be loaded.",
      empty: "You have no agents yet — create your first in six short steps.",
      edit: "Edit",
      editAria: "Edit {{name}}",
      openFailed: "This agent could not be opened.",
      noVersions: "This agent has no saved versions yet.",
      notWizard: "{{name}} was built in the advanced editor, so it opens there.",
      edited: "{{name}} was changed in the advanced editor, so it opens there to keep those changes.",
      dropped: "Not available in this installation, so left out: {{items}}",
      editingNew: "You are editing version {{ref}}. It is saved and can't change: in step 6 you can move it to the next stage, and your first change becomes version {{version}}.",
      editingDraft: "You are editing the draft of version {{ref}}.",
      status: { active: "Active", suspended: "Suspended", retired: "Retired" },
      deployed: "{{env}}: {{version}}",
      notReleased: "Not released yet",
      showRetired: "Show retired agents ({{count}})",
    },
    steps: {
      "1": { title: "What should it do?", short: "Purpose" },
      "2": { title: "What may it use?", short: "What it uses" },
      "3": { title: "When must a person approve?", short: "Approvals" },
      "4": { title: "How careful must it be with data?", short: "Data and model" },
      "5": { title: "Try it", short: "Try it" },
      "6": { title: "Save and release", short: "Save and release" },
    },
    nav: {
      back: "Back",
      next: "Next",
      cancel: "Cancel",
      advanced: "Advanced",
      advancedHint: "Open this draft in the visual graph editor and the spec view",
      stepOf: "Step {{n}} of {{total}}",
      problems_one: "{{count}} thing to fix",
      problems_other: "{{count}} things to fix",
    },
    starter: {
      question: "What kind of agent is it?",
      hint: "Pick the closest starting point. You can change it later.",
      example: "For example: {{example}}",
      underTheHood: "under the hood: {{pattern}}",
      answer_docs: { title: "Answer questions from documents", body: "Finds the answer in your documents and says where it found it.", example: "HR policy questions" },
      approval_request: { title: "Handle a request that needs approval", body: "Collects the details and prepares a change; a person approves before anything changes.", example: "pay an invoice, grant system access" },
      route_requests: { title: "Route requests to the right team", body: "Reads each request and sends it to the team that should handle it.", example: "a front desk for HR, IT and Finance" },
      fill_form: { title: "Fill a form from a document", body: "Reads a document, fills in the form fields and lists what is missing.", example: "supplier registration" },
      draft_review: { title: "Write a reply and check it", body: "Drafts a reply, checks it for accuracy and tone, and improves it.", example: "replies to customer emails" },
      look_up: { title: "Look things up and report", body: "Gets information from connected systems or allowed websites and answers with a clear summary and a table.", example: "invoice or order status, stock levels, free padel courts" },
      check_document: { title: "Check a document against rules", body: "Reviews a contract, invoice or application against your policies or a checklist and lists what is missing or risky.", example: "contract review, compliance check" },
      scheduled_report: { title: "Regular report or alert", body: "Runs on a schedule or when something happens, collects the data and sends a summary or a warning.", example: "a daily digest of overdue invoices, an alert when a spending limit is nearly used" },
      case_helper: { title: "Help with a case", body: "Works on a case: summarises it, finds related information and suggests the next step to the case worker.", example: "handling a customer complaint" },
      team: { title: "A team of agents", body: "Asks other agents of your organisation, each good at one kind of work, and gives one answer.", example: "a front desk for billing and support questions" },
      decide_and_ask: { title: "Let the agent decide, and ask a person when it isn't sure", body: "Picks an option (for example which team gets the request) and asks a person when it isn't confident.", example: "assigning tickets" },
    },
    pattern: { react: "reason and act (ReAct)", router: "router", plan_execute: "plan and execute", reflection: "draft and check (reflection)", supervisor: "a team of agents (supervisor or hand-over)" },
    purpose: {
      languages: "Which languages should it speak?",
      lang: { ar: "Arabic", en: "English", both: "Arabic and English" },
      title_en: "Name in English",
      title_ar: "Name in Arabic",
      titleExample_en: "For example: Leave balance helper",
      titleExample_ar: "For example: مساعد رصيد الإجازات",
      purpose_en: "What it does, in one sentence (English)",
      purpose_ar: "What it does, in one sentence (Arabic)",
      purposeExample_en: "For example: Answers staff questions about leave using the HR policies",
      purposeExample_ar: "For example: يجيب عن أسئلة الموظفين حول الإجازات من سياسات الموارد البشرية",
      technicalName: "Technical name",
      technicalNameHelp: "Filled in for you from the name. Change it only if you need a different link name: lowercase letters, digits and hyphens, at least 3 characters.",
      advanced: "Advanced",
      advancedPattern: "How it works inside: {{pattern}}",
      topics: "Which teams or topics should it send requests to?",
      topicsHint: "Requests that match none of them get a polite general answer.",
      topicN: "Team {{n}}",
      topicName_en: "Team {{n}} name (English)",
      topicName_ar: "Team {{n}} name (Arabic)",
      topicExample_en: "For example: HR",
      topicExample_ar: "For example: الموارد البشرية",
      removeTopic: "Remove team {{n}}",
      addTopic: "Add a team",
      audience: "Who will use it?",
      audienceExample: "For example: all staff, the accounts payable team",
      groups: "Only these groups may use it (optional)",
      groupsHelp: "Type a group name and press Enter. Leave empty so everyone in your organisation can use it.",
      instructions: "Anything else it should know? (optional)",
      instructionsExample: {
        answer_docs: "For example: Quote the policy section. If the policy doesn't say, tell the user.",
        approval_request: "For example: Never pay an invoice that is disputed.",
        route_requests: "For example: Payroll questions go to Finance.",
        fill_form: "For example: Fields: company name, registration number, VAT number, IBAN.",
        draft_review: "For example: Keep replies under 150 words and always thank the customer.",
        look_up: "For example: Invoice numbers look like INV-1043. Show number, supplier, amount and status in a table.",
        check_document: "For example: Checklist: payment terms, liability cap, termination notice, data protection clause.",
        scheduled_report: "For example: Send the summary to finance-team@example.com. Put the oldest invoices first.",
        case_helper: "For example: Suggest one next step at a time: reply, ask for a document, or escalate.",
        team: "For example: Answer in a friendly tone.",
      },
    },
    resources: {
      knowledgeTitle: "Knowledge it can search",
      knowledgeHint: "Collections of documents the agent reads before it answers. It only sees documents the user may see.",
      collectionsFailed: "Knowledge collections could not be loaded, so none can be chosen right now.",
      noCollections: "There are no knowledge collections yet. A knowledge curator can add them under Knowledge.",
      holds: "Holds {{level}} data",
      toolsTitle: "Systems it can use",
      toolsHint: "Switch a system on, then tick what the agent may do with it. Looking things up is safe; changing data needs care.",
      toolsFailed: "The systems could not be loaded.",
      noTools: "No systems are available yet.",
      toolLevel: "Holds up to {{level}} data",
      notCertified: "Not approved for use by business builders yet.",
      actionsFor: "What the agent may do with {{tool}}",
      reads: "Looks up",
      changes: "Changes data",
      highRisk: "High-risk action (risk level {{tier}}): agents made here can't use it.",
      roleNeeded: "Needs the {{roles}} role, which you don't have: only people with it can try or run the agent.",
      changeWarning: "This lets the agent change data in {{tool}}. You choose who approves these changes in the next step.",
      toolOff_one: "{{count}} thing it could do. Switch on to choose.",
      toolOff_other: "{{count}} things it could do. Switch on to choose.",
      actionLevel: "{{level}} data: ticking it raises this agent to {{level}}",
      actionLevelOn: "{{level}} data: only people cleared for {{level}} can use this agent",
      starterHint: {
        look_up: "This agent only looks things up. Switch on the systems it reads from and leave “Changes data” unticked. It may also make a PDF or Excel file of its report (Documents). The Web browser reads allowed websites when it is connected.",
        check_document: "Choose the knowledge collection that holds your policies or rules. People attach the document to check when they ask.",
        scheduled_report: "Choose where the data comes from. To send the summary by e-mail, switch on the e-mail system and tick its “Send” action; a person approves each message unless you choose otherwise in the next step.",
        case_helper: "Choose the knowledge and systems that hold related information. The agent doesn't change the case itself: it suggests the next step to the case worker.",
        team: "Choose the agents in the team and say what each is good at. Each member keeps its own systems, knowledge and approvals, so the team needs none of its own.",
      },
    },
    approval: {
      team: "A team asks for no approvals itself: each member asks for the approvals its own rules need, and they reach the reviewers as usual.",
      question: "When must a person approve?",
      hint: "Approvals appear in the reviewer inbox of the people you choose.",
      never: { title: "Never", body: "The agent acts and answers on its own." },
      before_changes: { title: "Before it changes anything", body: "A person approves every change before it happens. Looking things up needs no approval." },
      always: { title: "Always before answering", body: "A person checks every answer, and every change, before the requester gets it." },
      neverWarning: "The agent could change data without anyone checking first.",
      noChanges: "This agent doesn't change any data yet, so there is nothing to approve. Answers are not checked.",
      who: "Who approves?",
      whoHint: "Anyone with one of these roles can decide.",
      role: { approver: "Approvers", "finance-approver": "Finance approvers", reviewer: "Reviewers", "case-supervisor": "Case supervisors" },
      amount: "Only ask when the amount is above",
      amountHelp: "Only for actions that move money, such as a payment: other changes always ask. Leave empty to ask for every change. A payment without an amount always asks.",
      rulesTitle: "Platform rules that always apply",
      rule: {
        fourEyes: "The person who made a request can never approve it.",
        policy: "The platform may also ask for approval on its own: for high-risk actions, amounts above your organisation's limits, or requests influenced by untrusted content.",
        sla: "Approvals are due within 4 hours.",
        risk: "Risk level: {{tier}}.",
      },
      tier: { "1": "low (looks things up only)", "2": "medium (changes data or handles confidential data)" },
      rulesSource: "These are the platform's own rules; this agent cannot switch them off.",
    },
    data: {
      question: "How careful must it be with data?",
      hint: "The agent never handles data above this level, even if a user asks.",
      level: {
        public: "Anyone may see it. For example: published prices, office hours.",
        internal: "Staff only. For example: internal policies, the org chart.",
        confidential: "A limited group. For example: invoices, contracts, salaries.",
        restricted: "Strictly limited. For example: health records, security incidents.",
      },
      modelTitle: "Which AI model should it use?",
      modelHint: "These are the models your administrator approved.",
      modelsFailed: "The AI models could not be loaded.",
      noModels: "No AI models are approved yet. Ask a model administrator.",
      onlineRule: "Platform rule: Confidential and Restricted data are never sent to online services.",
      badge: { local: "Runs on your servers", online: "Online service", mixed: "Partly online" },
      answeredBy: "Answers come from {{model}} on {{provider}} ({{where}}).",
      privateBy: "Confidential data: {{model}}, on your servers.",
      noAnswer: "No model can answer with this choice right now. Ask your platform team.",
      whereIsSet: "Your platform team decides which model sits behind each choice (Models › Model choices).",
      maxLevel: "Up to {{level}} data",
      splitLevels: "Online up to {{online}} data; on your servers up to {{local}}",
      localOnly: "{{level}} data: your servers only, simpler answers",
      cannotSee: "May not see {{level}} data",
      localOnlyNote: "With {{level}} data, {{model}} uses only the model on your own servers. It is smaller, so answers are simpler and it is less reliable at using systems. Keep {{level}} if your data needs it; choose Internal only if the data allows.",
      localStrong: "{{level}} data: your servers only, stronger private model",
      localOnlyNoteStrong: "With {{level}} data, {{model}} uses the stronger private model on your own servers. It uses systems and fills forms well, in Arabic and English, but answers more slowly than online models.",
      strongerHint: "Your platform team can turn on a stronger private model for {{level}} data (better with systems and forms). Ask them if this agent needs it.",
      audienceLevel: "Only people cleared for {{level}} data can use this agent. Everyone else, for example employees without that clearance, is refused when they ask it something.",
      audienceLevelFor: "It is meant for “{{audience}}”: check that all of them are cleared for {{level}} data, or lower the level.",
      levelBecause: "It needs {{level}} because of: {{items}}. To lower the level, untick these in step 2.",
      levelChosen: "You chose this level. Choose Internal if the data allows, so more people can use it.",
      levelCollection: "the knowledge {{collection}}",
      devOnly: "For trying out in Development",
      approvedFor: "Approved for {{envs}}",
      modelName: { "chat-default": "Standard", "chat-small": "Fast and low-cost", "qwen-local": "Local test model", "chat-local-strong": "Stronger private" },
    },
    env: { dev: "Development", test: "Testing", staging: "Final check", prod: "Live" },
    classification: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
    listSep: ", ",
    try: {
      fixFirst: "Fix these first, then come back to try it:",
      intro: "Talk to your agent here the way a user would. It runs in Development: look-ups in other systems run for real, actions that would change something are only simulated, approval steps are skipped, and when the AI is not sure it goes on with its own suggestion.",
      versionLine: "Trying saves this as version {{version}} in Development — your next change becomes version {{next}}.",
      start: "Try it",
      restart: "Try my changes",
      skip: "You can also skip this step and test later.",
      saved: "Version {{ref}} is in Development. Ask it what a user would ask.",
      conversation: "Test conversation",
      empty: "No messages yet.",
      startFailed: "The test could not start.",
      runFailed: "The test run stopped with an error.",
      reason: "Tried in the guided builder",
      phase: {
        saving: "Saving version {{version}}…",
        releasing: "Putting version {{version}} in Development…",
        waiting: "Waiting for Development to have version {{version}}…",
        policy: "Almost ready: the rules are picking up the new version…",
      },
      error: {
        save: "Version {{version}} could not be saved.",
        release: "Version {{version}} could not be put in Development.",
        wait: "Development did not have version {{version}} ready in time. Try again in a minute.",
      },
      inputLabel: "Your test message",
      placeholder: "Ask what a user would ask…",
      send: "Send",
      progress: "Progress",
    },
    release: {
      saveAs: "Save as version {{version}}",
      isSaved: "Version {{version}} is saved and can't change. Change any answer and it becomes version {{next}}.",
      versionLabel: "Version {{version}}",
      savedAs: "Saved as version {{version}}",
      toFinal: "Ask for the final check",
      toFinalHint: "Its quality checks run, then a release approver who isn't you signs off.",
      toLive: "Ask to go Live",
      toLiveHint: "Two release approvers who aren't you sign off; then people can use it.",
      autonomous: "It works on its own: its schedule or event starts it, with nobody signed in. In each stage it runs that stage's version.",
      goTo: "Step {{n}}:",
      checksTitle: "Checks",
      allGood: "Everything checks out.",
      dataChecks_one: "The rules are met. {{count}} thing to look at in how the agent's steps use data (the same check as the Checks panel in the advanced editor):",
      dataChecks_other: "The rules are met. {{count}} things to look at in how the agent's steps use data (the same checks as the Checks panel in the advanced editor):",
      changedAfterSubmit: "You changed answers after version {{version}} was saved. A saved version can't change, so your changes become version {{next}}.",
      bump: "Make a new version",
      saveDraft: "Save as a draft",
      submit: "Submit for release",
      submitConfirm: "Save version {{version}}? After this it can't change; later changes become the next version.",
      submitHint: "Saving freezes version {{version}} so it can be released. A draft can still change but can't be released.",
      stagesTitle: "Release stages",
      stage: {
        draft: { title: "Draft", body: "Only you can see and test it." },
        dev: { title: "Development", body: "Builders can try it in the Assistant. No sign-off needed." },
        test: { title: "Testing", body: "A release approver who isn't you signs off." },
        live: { title: "Live", body: "Needs passing quality checks and a release approver's sign-off." },
      },
      next: { save: "Save this version to start the release.", submit: "Save the draft as a version to release it to Development.", saveChanges: "Save your changes as version {{version}} to release them." },
      waiting: "Waiting for {{stage}}",
      waitingEvals: "Its quality checks have to pass first.",
      waitingSignOff: "A release approver who isn't you signs it off in Governance › Releases. Builders can't approve their own releases.",
      toDev: "Release to Development",
      toDevHint: "Happens straight away; no sign-off needed.",
      toTest: "Ask for Testing",
      toTestHint: "A release approver who isn't you will be asked to sign off.",
      liveHow: "Final check and Live are requested from Governance › Releases once the quality checks (reference and attack tests) pass.",
      isLive: "This version is Live.",
      rejected: "The last release request was rejected. See Governance › Releases for the reason.",
      promotionFailed: "The release request failed",
      successTitle: "{{ref}} is saved",
      successBody: "It is frozen and ready to release. Once it is in Development you can use it in the Assistant.",
      openAssistant: "Open in Assistant",
      openGovernance: "See its page in Governance",
      another: "Create another agent",
      reason: "Requested from the guided builder",
      promoted: "{{ref}} is now in {{stage}}",
      requested: "Asked for {{stage}} for {{ref}}",
      draftSaved: "Draft saved: {{ref}}",
      submitted: "Submitted: {{ref}}",
      unchanged: "{{ref}} was already submitted with these answers",
      useVersion: "Use version {{version}}",
      error: {
        "409": "This version is already submitted and can't change",
        "422": "The platform did not accept this agent",
        "403": "You are not allowed to save agents",
        other: "The agent could not be saved",
      },
    },
    summary: {
      team_supervisor: "A supervisor asks: {{members}}",
      team_handoff: "Hands the conversation between: {{members}}",
      autonomous: "Works on its own when its schedule or event starts it",
      title: "What your agent will do",
      unnamed: "Your new agent",
      purposeMissing: "Tell us in step 1 what it should do.",
      someone: "someone",
      tool: "{{tool}}: {{actions}}",
      approval: {
        never: "Changes data without asking anyone first.",
        readOnly: "Only looks things up; it never changes anything.",
        before_changes: "Asks {{roles}} before it changes anything.",
        before_changes_amount: "Asks {{roles}} before changes above {{amount}}.",
        before_changes_amount_some: "Asks {{roles}} before it changes anything; payments only above {{amount}}.",
        always: "{{roles}} check every answer and change.",
      },
      languages: "Speaks {{langs}}",
      audience: "Used by {{audience}}",
      groups: "Only for the groups {{groups}}",
      topics: "Sends requests to {{topics}}",
      knowledge: "Searches {{list}}",
      nothing: "Uses no documents or systems: it answers from what the AI model knows.",
      data: "Handles up to {{level}} data",
      model: "Uses the {{model}} AI model",
      ready: "Ready to save",
      stepProblem: "Step {{n}}: {{text}}",
      technicalName: "Technical name and version",
    },
    problem: {
      team_members: "Add at least two agents to the team.",
      team_too_many: "A team can have at most 8 members.",
      team_description: "Say what {{member}} is good at, so the team knows when to ask it.",
      team_role: "Give {{member}} a name in the team.",
      team_limit: "The limit must be between 1 and 50.",
      trigger_unavailable: "This way of starting never starts the agent yet (new files and queue messages are coming later). Choose a schedule, an e-mail or another system calling it.",
      autonomous_unchecked: "It works on its own and changes data with nobody checking. Choose \"Before it changes anything\" or \"Always\", or switch off working on its own in step 1.",
      title_en: "Give it a name in English.",
      title_ar: "Give it a name in Arabic.",
      purpose_en: "Say in English what it does.",
      purpose_ar: "Say in Arabic what it does.",
      name: "Choose a technical name: lowercase letters, digits and hyphens, starting with a letter, 3 to 41 characters.",
      version: "The version must look like 1.0.0.",
      topics: "Add at least one team or topic.",
      topic_empty: "Team {{n}} needs a name.",
      sources: "Choose at least one knowledge collection or system it can answer from.",
      change_needed: "Choose at least one thing that changes data: this starting point is for requests that need approval.",
      tool_missing: "{{tool}} isn't available here. Remove it.",
      tool_uncertified: "{{tool}} isn't approved for business builders.",
      tool_no_actions: "Tick what the agent may do with {{tool}}, or switch it off.",
      approver: "Choose who approves.",
      approval_needed: "This starting point needs a person to approve changes. Choose \"Before it changes anything\" or \"Always\".",
      amount: "The amount must be more than 0, or left empty.",
      collection_missing: "The collection {{collection}} isn't available here.",
      collection_level: "{{collection}} holds {{level}} data. Choose {{level}} or a stricter level.",
      model: "Choose an AI model.",
      model_missing: "The AI model {{model}} isn't available here. Choose another one.",
      model_level: "{{model}} can handle up to {{level}} data. Choose a lower level or another model.",
      tool_level: "{{tool}} holds {{level}} data, so an agent below {{level}} is refused when it uses it. Choose {{level}} or a stricter level.",
      action_tier: "“{{action}}” in {{tool}} is a high-risk action (risk level {{tier}}). Agents made here go up to level 2: untick it, or ask a developer to build this agent in the advanced editor.",
      model_online: "{{model}} is an online service, and Confidential or Restricted data never go to online services. Choose a model that runs on your servers.",
      technical: "A technical check failed: {{detail}}",
      tools_needed: "Switch on at least one system it can get information from.",
      knowledge_needed: "Choose the knowledge collection with the rules to check against.",
      reads_only: "This agent only looks things up. Untick the actions that change data, or start from “Handle a request that needs approval”.",
      schedule: "Choose when it runs: on a schedule or when something happens.",
      schedule_incomplete: "Finish the “When does it run?” answer: something is still missing.",
    },
    handoff: {
      group: "From the guided flow",
      option: "Your draft ({{ref}})",
      fromWizard: "This is the agent from the guided flow, shown as a graph.",
      separate: "Changes you make here stay in the advanced editor. After that, the agent opens here rather than in the guided flow.",
      back: "Back to the guided flow",
    },
  },
  ar: {
    page: {
      subtitle: "أنشئ الوكلاء وأطلقهم بخطوات بسيطة، أو افتح المحررات المتقدمة.",
      wizardTitle: "إنشاء وكيل",
      wizardSubtitle: "سؤال واحد في كل خطوة. يعرض الملخص الجانبي ما سيفعله وكيلك.",
      home: "كل الوكلاء",
      advanced: "المحرر المتقدم",
      guided: "إنشاء وكيل (موجَّه)",
    },
    home: {
      heroTitle: "أنشئ وكيلًا في ست خطوات قصيرة",
      heroBody: "أجب عن بضعة أسئلة بكلمات بسيطة، ونحن نبني الوكيل ونتحقق منه وفق قواعد المنصة ونساعدك على إطلاقه.",
      start: "إنشاء وكيل",
      templatesTitle: "ابدأ من مثال جاهز",
      templatesHint: "يملأ المثال الإجابات عنك، ويمكنك تغيير أي شيء قبل الحفظ.",
      useTemplate: "استخدم هذا المثال",
      useTemplateAria: "استخدم المثال {{name}}",
      agentsTitle: "وكلاؤك",
      agentsFailed: "تعذّر تحميل وكلائك.",
      empty: "ليس لديك وكلاء بعد — أنشئ أول وكيل في ست خطوات قصيرة.",
      edit: "تعديل",
      editAria: "تعديل {{name}}",
      openFailed: "تعذّر فتح هذا الوكيل.",
      noVersions: "لا توجد نسخ محفوظة لهذا الوكيل بعد.",
      notWizard: "بُني {{name}} في المحرر المتقدم، لذلك يُفتح هناك.",
      edited: "عُدِّل {{name}} في المحرر المتقدم، لذلك يُفتح هناك للحفاظ على تلك التعديلات.",
      dropped: "غير متاح في هذا التثبيت، لذلك استُبعد: {{items}}",
      editingNew: "أنت تعدّل الإصدار {{ref}}. إنه محفوظ ولا يمكن تغييره: يمكنك نقله إلى المرحلة التالية في الخطوة 6، وأول تعديل منك يصبح الإصدار {{version}}.",
      editingDraft: "أنت تعدّل مسودة الإصدار {{ref}}.",
      status: { active: "نشط", suspended: "موقوف", retired: "متقاعد" },
      deployed: "{{env}}: {{version}}",
      notReleased: "لم يُطلق بعد",
      showRetired: "إظهار الوكلاء المتقاعدين ({{count}})",
    },
    steps: {
      "1": { title: "ماذا يجب أن يفعل؟", short: "الغرض" },
      "2": { title: "ما الذي يُسمح له باستخدامه؟", short: "ما يستخدمه" },
      "3": { title: "متى يجب أن يوافق شخص؟", short: "الموافقات" },
      "4": { title: "ما مدى الحرص المطلوب مع البيانات؟", short: "البيانات والنموذج" },
      "5": { title: "جرّبه", short: "التجربة" },
      "6": { title: "الحفظ والإطلاق", short: "الحفظ والإطلاق" },
    },
    nav: {
      back: "السابق",
      next: "التالي",
      cancel: "إلغاء",
      advanced: "متقدم",
      advancedHint: "افتح هذه المسودة في محرر المخطط المرئي وعرض المواصفة",
      stepOf: "الخطوة {{n}} من {{total}}",
      problems_zero: "لا شيء يحتاج إلى إصلاح",
      problems_one: "أمر واحد يحتاج إلى إصلاح",
      problems_two: "أمران يحتاجان إلى إصلاح",
      problems_few: "{{count}} أمور تحتاج إلى إصلاح",
      problems_many: "{{count}} أمرًا يحتاج إلى إصلاح",
      problems_other: "{{count}} أمر يحتاج إلى إصلاح",
    },
    starter: {
      question: "ما نوع هذا الوكيل؟",
      hint: "اختر أقرب نقطة بداية، ويمكنك تغييرها لاحقًا.",
      example: "مثال: {{example}}",
      underTheHood: "في الخلفية: {{pattern}}",
      answer_docs: { title: "الإجابة عن الأسئلة من المستندات", body: "يجد الإجابة في مستنداتك ويذكر أين وجدها.", example: "أسئلة سياسات الموارد البشرية" },
      approval_request: { title: "معالجة طلب يحتاج إلى موافقة", body: "يجمع التفاصيل ويجهّز التغيير، ويوافق شخص قبل أن يتغير أي شيء.", example: "سداد فاتورة، منح صلاحية على نظام" },
      route_requests: { title: "توجيه الطلبات إلى الفريق المناسب", body: "يقرأ كل طلب ويرسله إلى الفريق الذي يجب أن يتولاه.", example: "مكتب استقبال للموارد البشرية وتقنية المعلومات والمالية" },
      fill_form: { title: "تعبئة نموذج من مستند", body: "يقرأ المستند ويملأ حقول النموذج ويذكر ما ينقص.", example: "تسجيل المورّدين" },
      draft_review: { title: "كتابة رد ومراجعته", body: "يكتب مسودة الرد ويراجع دقتها وأسلوبها ثم يحسّنها.", example: "الرد على رسائل العملاء" },
      look_up: { title: "البحث عن معلومات وعرضها", body: "يجلب المعلومات من الأنظمة المتصلة أو المواقع المسموح بها ويجيب بملخص واضح وجدول.", example: "حالة فاتورة أو طلب، مستويات المخزون، ملاعب بادل متاحة" },
      check_document: { title: "مراجعة مستند مقابل القواعد", body: "يراجع عقدًا أو فاتورة أو طلبًا مقابل سياساتكم أو قائمة تحقق ويذكر الناقص أو الخطر.", example: "مراجعة العقود، فحص الامتثال" },
      scheduled_report: { title: "تقرير دوري أو تنبيه", body: "يعمل وفق جدول أو عند حدوث أمر ما، فيجمع البيانات ويرسل ملخصًا أو تحذيرًا.", example: "ملخص يومي للفواتير المتأخرة، تنبيه عند اقتراب استهلاك حد الإنفاق" },
      case_helper: { title: "المساعدة في قضية", body: "يعمل داخل القضية: يلخصها ويجد المعلومات المتعلقة بها ويقترح الخطوة التالية على موظف القضية.", example: "معالجة شكوى عميل" },
      team: { title: "فريق من الوكلاء", body: "يسأل وكلاء آخرين في مؤسستك، يجيد كل منهم نوعًا من العمل، ويعطي إجابة واحدة.", example: "مكتب استقبال لأسئلة الفوترة والدعم" },
      decide_and_ask: { title: "دع الوكيل يقرر، واسأل شخصًا عندما لا يكون متأكدًا", body: "يختار خيارًا (مثل الفريق الذي يستلم الطلب) ويسأل شخصًا عندما لا يكون واثقًا.", example: "إسناد التذاكر" },
    },
    pattern: { react: "التفكير ثم التنفيذ (ReAct)", router: "الموجِّه", plan_execute: "التخطيط ثم التنفيذ", reflection: "المسودة ثم المراجعة", supervisor: "فريق من الوكلاء (مشرف أو تسليم)" },
    purpose: {
      languages: "ما اللغات التي يجب أن يتحدث بها؟",
      lang: { ar: "العربية", en: "الإنجليزية", both: "العربية والإنجليزية" },
      title_en: "الاسم بالإنجليزية",
      title_ar: "الاسم بالعربية",
      titleExample_en: "مثال: Leave balance helper",
      titleExample_ar: "مثال: مساعد رصيد الإجازات",
      purpose_en: "ماذا يفعل، في جملة واحدة (بالإنجليزية)",
      purpose_ar: "ماذا يفعل، في جملة واحدة (بالعربية)",
      purposeExample_en: "مثال: Answers staff questions about leave using the HR policies",
      purposeExample_ar: "مثال: يجيب عن أسئلة الموظفين حول الإجازات من سياسات الموارد البشرية",
      technicalName: "الاسم التقني",
      technicalNameHelp: "يُملأ تلقائيًا من الاسم. غيّره فقط إن احتجت اسمًا مختلفًا للرابط: أحرف إنجليزية صغيرة وأرقام وشرطات، ثلاثة أحرف على الأقل.",
      advanced: "خيارات متقدمة",
      advancedPattern: "طريقة عمله من الداخل: {{pattern}}",
      topics: "إلى أي الفرق أو المواضيع يرسل الطلبات؟",
      topicsHint: "الطلبات التي لا تطابق أيًّا منها تحصل على رد عام مهذب.",
      topicN: "الفريق {{n}}",
      topicName_en: "اسم الفريق {{n}} (بالإنجليزية)",
      topicName_ar: "اسم الفريق {{n}} (بالعربية)",
      topicExample_en: "مثال: HR",
      topicExample_ar: "مثال: الموارد البشرية",
      removeTopic: "إزالة الفريق {{n}}",
      addTopic: "إضافة فريق",
      audience: "من سيستخدمه؟",
      audienceExample: "مثال: جميع الموظفين، فريق الحسابات الدائنة",
      groups: "مقتصر على هذه المجموعات فقط (اختياري)",
      groupsHelp: "اكتب اسم المجموعة واضغط Enter. اتركه فارغًا ليتمكن الجميع في مؤسستك من استخدامه.",
      instructions: "هل هناك أي شيء آخر يجب أن يعرفه؟ (اختياري)",
      instructionsExample: {
        answer_docs: "مثال: اذكر بند السياسة. وإذا لم تذكر السياسة شيئًا فأخبر المستخدم.",
        approval_request: "مثال: لا تسدد أبدًا فاتورة عليها نزاع.",
        route_requests: "مثال: أسئلة الرواتب تذهب إلى المالية.",
        fill_form: "مثال: الحقول: اسم الشركة، رقم السجل التجاري، الرقم الضريبي، الآيبان.",
        draft_review: "مثال: اجعل الرد أقل من 150 كلمة واشكر العميل دائمًا.",
        look_up: "مثال: أرقام الفواتير مثل INV-1043. اعرض الرقم والمورّد والمبلغ والحالة في جدول.",
        check_document: "مثال: قائمة التحقق: شروط الدفع، سقف المسؤولية، مدة إشعار الإنهاء، بند حماية البيانات.",
        scheduled_report: "مثال: أرسل الملخص إلى finance-team@example.com. ضع الفواتير الأقدم أولًا.",
        case_helper: "مثال: اقترح خطوة تالية واحدة في كل مرة: الرد، أو طلب مستند، أو التصعيد.",
        team: "مثال: أجب بأسلوب ودود.",
      },
    },
    resources: {
      knowledgeTitle: "المعرفة التي يمكنه البحث فيها",
      knowledgeHint: "مجموعات مستندات يقرؤها الوكيل قبل أن يجيب، ولا يرى إلا ما يحق للمستخدم رؤيته.",
      collectionsFailed: "تعذّر تحميل مجموعات المعرفة، لذلك لا يمكن اختيار أيٍّ منها الآن.",
      noCollections: "لا توجد مجموعات معرفة بعد. يمكن لأمين المعرفة إضافتها من صفحة المعرفة.",
      holds: "تحتوي بيانات {{level}}",
      toolsTitle: "الأنظمة التي يمكنه استخدامها",
      toolsHint: "فعّل النظام ثم حدّد ما يُسمح للوكيل بفعله به. البحث آمن، أما تغيير البيانات فيحتاج إلى حرص.",
      toolsFailed: "تعذّر تحميل الأنظمة.",
      noTools: "لا توجد أنظمة متاحة بعد.",
      toolLevel: "يحتوي بيانات حتى مستوى {{level}}",
      notCertified: "غير معتمد بعد لاستخدام منشئي الأعمال.",
      actionsFor: "ما يُسمح للوكيل بفعله في {{tool}}",
      reads: "يبحث",
      changes: "يغيّر البيانات",
      highRisk: "إجراء عالي الخطورة (مستوى الخطورة {{tier}}): لا يستطيع الوكلاء المصنوعون هنا استخدامه.",
      roleNeeded: "يحتاج إلى دور {{roles}}، وهو ليس لديك: لا يجربه أو يشغّله إلا من لديه هذا الدور.",
      changeWarning: "يسمح هذا للوكيل بتغيير البيانات في {{tool}}. ستختار في الخطوة التالية من يوافق على هذه التغييرات.",
      toolOff_zero: "لا يوجد ما يمكنه فعله.",
      toolOff_one: "أمر واحد يمكنه فعله. فعّله للاختيار.",
      toolOff_two: "أمران يمكنه فعلهما. فعّله للاختيار.",
      toolOff_few: "{{count}} أمور يمكنه فعلها. فعّله للاختيار.",
      toolOff_many: "{{count}} أمرًا يمكنه فعله. فعّله للاختيار.",
      toolOff_other: "{{count}} أمر يمكنه فعله. فعّله للاختيار.",
      actionLevel: "بيانات {{level}}: تحديده يرفع مستوى هذا الوكيل إلى {{level}}",
      actionLevelOn: "بيانات {{level}}: لا يستخدم هذا الوكيل إلا من لديهم تصريح {{level}}",
      starterHint: {
        look_up: "هذا الوكيل يبحث عن المعلومات فقط. فعّل الأنظمة التي يقرأ منها واترك «يغيّر البيانات» دون تحديد. ويمكنه أيضًا إنشاء ملف PDF أو Excel لتقريره (المستندات). يقرأ متصفح الويب المواقع المسموح بها عندما يكون متصلًا.",
        check_document: "اختر مجموعة المعرفة التي تضم سياساتكم أو قواعدكم. يرفق الناس المستند المراد فحصه عند السؤال.",
        scheduled_report: "اختر مصدر البيانات. لإرسال الملخص بالبريد، فعّل نظام البريد الإلكتروني وحدّد إجراء «إرسال»؛ يوافق شخص على كل رسالة ما لم تختر غير ذلك في الخطوة التالية.",
        case_helper: "اختر المعرفة والأنظمة التي تضم المعلومات المتعلقة. لا يغيّر الوكيل القضية بنفسه، بل يقترح الخطوة التالية على موظف القضية.",
        team: "اختر الوكلاء في الفريق واذكر ما يجيده كل منهم. يحتفظ كل عضو بأنظمته ومعرفته واعتماداته، فلا يحتاج الفريق إلى شيء منها.",
      },
    },
    approval: {
      team: "لا يطلب الفريق اعتمادات بنفسه: يطلب كل عضو الاعتمادات التي تحتاجها قواعده، وتصل إلى المراجعين كالمعتاد.",
      question: "متى يجب أن يوافق شخص؟",
      hint: "تظهر طلبات الموافقة في صندوق المراجعة لدى الأشخاص الذين تختارهم.",
      never: { title: "أبدًا", body: "يتصرف الوكيل ويجيب بنفسه." },
      before_changes: { title: "قبل أن يغيّر أي شيء", body: "يوافق شخص على كل تغيير قبل حدوثه. البحث لا يحتاج إلى موافقة." },
      always: { title: "دائمًا قبل الإجابة", body: "يراجع شخص كل إجابة وكل تغيير قبل أن تصل إلى صاحب الطلب." },
      neverWarning: "قد يغيّر الوكيل البيانات دون أن يراجعها أحد أولًا.",
      noChanges: "لا يغيّر هذا الوكيل أي بيانات بعد، لذلك لا يوجد ما يُوافَق عليه. ولا تُراجَع الإجابات.",
      who: "من يوافق؟",
      whoHint: "يمكن لأي شخص يحمل أحد هذه الأدوار أن يقرر.",
      role: { approver: "المعتمِدون", "finance-approver": "المعتمِدون الماليون", reviewer: "المراجِعون", "case-supervisor": "مشرفو الحالات" },
      amount: "اطلب الموافقة فقط عندما يتجاوز المبلغ",
      amountHelp: "ينطبق فقط على الإجراءات التي تنقل المال، مثل الدفع: التغييرات الأخرى تطلب الموافقة دائمًا. اتركه فارغًا لطلب الموافقة على كل تغيير. الدفع الذي لا يتضمن مبلغًا يطلب الموافقة دائمًا.",
      rulesTitle: "قواعد المنصة التي تنطبق دائمًا",
      rule: {
        fourEyes: "لا يمكن لصاحب الطلب أن يوافق عليه أبدًا.",
        policy: "قد تطلب المنصة الموافقة من تلقاء نفسها: للإجراءات عالية المخاطر، أو للمبالغ التي تتجاوز حدود مؤسستك، أو للطلبات المتأثرة بمحتوى غير موثوق.",
        sla: "يجب البت في الموافقات خلال 4 ساعات.",
        risk: "مستوى المخاطر: {{tier}}.",
      },
      tier: { "1": "منخفض (يبحث فقط)", "2": "متوسط (يغيّر البيانات أو يتعامل مع بيانات سرية)" },
      rulesSource: "هذه قواعد المنصة نفسها، ولا يمكن لهذا الوكيل إيقافها.",
    },
    data: {
      question: "ما مدى الحرص المطلوب مع البيانات؟",
      hint: "لن يتعامل الوكيل أبدًا مع بيانات أعلى من هذا المستوى، حتى لو طلب المستخدم.",
      level: {
        public: "يمكن لأي شخص رؤيتها. مثال: الأسعار المنشورة، ساعات العمل.",
        internal: "للموظفين فقط. مثال: السياسات الداخلية، الهيكل التنظيمي.",
        confidential: "لمجموعة محدودة. مثال: الفواتير، العقود، الرواتب.",
        restricted: "محدودة بشدة. مثال: السجلات الصحية، الحوادث الأمنية.",
      },
      modelTitle: "أي نموذج ذكاء اصطناعي يجب أن يستخدم؟",
      modelHint: "هذه هي النماذج التي اعتمدها مسؤول المنصة.",
      modelsFailed: "تعذّر تحميل نماذج الذكاء الاصطناعي.",
      noModels: "لا توجد نماذج معتمدة بعد. تواصل مع مسؤول النماذج.",
      onlineRule: "قاعدة المنصة: لا تُرسَل البيانات السرية أو المقيّدة أبدًا إلى خدمات عبر الإنترنت.",
      badge: { local: "يعمل على خوادمك", online: "خدمة عبر الإنترنت", mixed: "جزئيًا عبر الإنترنت" },
      answeredBy: "الإجابات من {{model}} لدى {{provider}} ({{where}}).",
      privateBy: "البيانات السرية: {{model}}، على خوادمك.",
      noAnswer: "لا يوجد نموذج يمكنه الإجابة بهذا الاختيار الآن. تواصل مع فريق المنصة.",
      whereIsSet: "يحدد فريق المنصة النموذج الذي يقف خلف كل اختيار (النماذج › خيارات النماذج).",
      maxLevel: "حتى بيانات {{level}}",
      splitLevels: "عبر الإنترنت حتى بيانات {{online}}؛ على خوادمك حتى {{local}}",
      localOnly: "بيانات {{level}}: على خوادمك فقط، وإجابات أبسط",
      cannotSee: "لا يجوز أن يرى بيانات {{level}}",
      localOnlyNote: "مع بيانات {{level}}، يستخدم {{model}} النموذج الذي يعمل على خوادمك فقط. وهو أصغر، لذا تكون الإجابات أبسط ويكون أقل إتقانًا لاستخدام الأنظمة. أبقِ {{level}} إن كانت بياناتك تتطلبه؛ ولا تختر «داخلي» إلا إذا سمحت البيانات بذلك.",
      localStrong: "بيانات {{level}}: على خوادمك فقط، بالنموذج الخاص الأقوى",
      localOnlyNoteStrong: "مع بيانات {{level}}، يستخدم {{model}} النموذج الخاص الأقوى الذي يعمل على خوادمك. وهو يتقن استخدام الأنظمة وملء النماذج بالعربية والإنجليزية، لكنه يجيب أبطأ من النماذج عبر الإنترنت.",
      strongerHint: "يستطيع فريق المنصة تشغيل نموذج خاص أقوى لبيانات {{level}} (أفضل مع الأنظمة والنماذج). اطلب منهم ذلك إن احتاجه هذا الوكيل.",
      audienceLevel: "لا يستطيع استخدام هذا الوكيل إلا من لديهم تصريح لبيانات {{level}}. أما الآخرون، مثل الموظفين الذين ليس لديهم هذا التصريح، فيُرفض طلبهم عندما يسألونه.",
      audienceLevelFor: "هو مخصص لـ «{{audience}}»: تأكد أن لديهم جميعًا تصريحًا لبيانات {{level}}، أو اخفض المستوى.",
      levelBecause: "يحتاج إلى مستوى {{level}} بسبب: {{items}}. لخفض المستوى، ألغِ تحديد هذه في الخطوة 2.",
      levelChosen: "أنت اخترت هذا المستوى. اختر «داخلية» إن سمحت البيانات، ليتمكن عدد أكبر من الأشخاص من استخدامه.",
      levelCollection: "المعرفة {{collection}}",
      devOnly: "للتجربة في مرحلة التطوير",
      approvedFor: "معتمد لـ {{envs}}",
      modelName: { "chat-default": "القياسي", "chat-small": "السريع منخفض التكلفة", "qwen-local": "نموذج اختبار محلي", "chat-local-strong": "الخاص الأقوى" },
    },
    env: { dev: "التطوير", test: "الاختبار", staging: "ما قبل الإطلاق", prod: "الإطلاق" },
    classification: { public: "عامة", internal: "داخلية", confidential: "سرية", restricted: "مقيّدة" },
    listSep: "، ",
    try: {
      fixFirst: "أصلح هذه الأمور أولًا، ثم عد لتجربته:",
      intro: "تحدّث مع وكيلك هنا كما يفعل المستخدم. يعمل في مرحلة التطوير: عمليات الاستعلام في الأنظمة الأخرى تُنفَّذ فعليًا، والإجراءات التي تغيّر شيئًا محاكاة فقط، وخطوات الموافقة تُتخطّى، وعندما لا يكون الذكاء الاصطناعي متأكدًا يتابع باقتراحه.",
      versionLine: "التجربة تحفظ هذا كالإصدار {{version}} في التطوير — وتعديلك التالي يصبح الإصدار {{next}}.",
      start: "جرّبه",
      restart: "جرّب تعديلاتي",
      skip: "يمكنك أيضًا تخطي هذه الخطوة والتجربة لاحقًا.",
      saved: "الإصدار {{ref}} في مرحلة التطوير. اسأله ما قد يسأله المستخدم.",
      conversation: "محادثة التجربة",
      empty: "لا توجد رسائل بعد.",
      startFailed: "تعذّر بدء التجربة.",
      runFailed: "توقفت التجربة بسبب خطأ.",
      reason: "تمت تجربته في المنشئ الموجَّه",
      phase: {
        saving: "جارٍ حفظ الإصدار {{version}}…",
        releasing: "جارٍ وضع الإصدار {{version}} في التطوير…",
        waiting: "بانتظار جاهزية الإصدار {{version}} في التطوير…",
        policy: "يكاد يكون جاهزًا: القواعد تستلم الإصدار الجديد…",
      },
      error: {
        save: "تعذّر حفظ الإصدار {{version}}.",
        release: "تعذّر وضع الإصدار {{version}} في التطوير.",
        wait: "لم يصبح الإصدار {{version}} جاهزًا في التطوير في الوقت المحدد. حاول مجددًا بعد دقيقة.",
      },
      inputLabel: "رسالة التجربة",
      placeholder: "اسأل ما قد يسأله المستخدم…",
      send: "إرسال",
      progress: "سير العمل",
    },
    release: {
      saveAs: "احفظه كالإصدار {{version}}",
      isSaved: "الإصدار {{version}} محفوظ ولا يمكن تغييره. غيّر أي إجابة ليصبح الإصدار {{next}}.",
      versionLabel: "الإصدار {{version}}",
      savedAs: "حُفظ كالإصدار {{version}}",
      toFinal: "اطلب الفحص النهائي",
      toFinalHint: "تُجرى فحوص الجودة، ثم يعتمده معتمِد إطلاق غيرك.",
      toLive: "اطلب التشغيل الفعلي",
      toLiveHint: "يعتمده معتمِدا إطلاق غيرك؛ ثم يمكن للناس استخدامه.",
      autonomous: "يعمل وحده: يبدأه جدوله أو حدثه دون أن يسجّل أحد دخوله. في كل مرحلة يعمل إصدار تلك المرحلة.",
      goTo: "الخطوة {{n}}:",
      checksTitle: "التحقق",
      allGood: "كل شيء سليم.",
      dataChecks_zero: "القواعد مستوفاة.",
      dataChecks_two: "القواعد مستوفاة. هناك أمران يستحقان النظر في طريقة استخدام خطوات الوكيل للبيانات (الفحوصات نفسها في لوحة الفحوصات في المحرر المتقدم):",
      dataChecks_few: "القواعد مستوفاة. هناك {{count}} أمور تستحق النظر في طريقة استخدام خطوات الوكيل للبيانات (الفحوصات نفسها في لوحة الفحوصات في المحرر المتقدم):",
      dataChecks_many: "القواعد مستوفاة. هناك {{count}} أمرًا يستحق النظر في طريقة استخدام خطوات الوكيل للبيانات (الفحوصات نفسها في لوحة الفحوصات في المحرر المتقدم):",
      dataChecks_one: "القواعد مستوفاة. هناك أمر واحد يستحق النظر في طريقة استخدام خطوات الوكيل للبيانات (الفحص نفسه في لوحة الفحوصات في المحرر المتقدم):",
      dataChecks_other: "القواعد مستوفاة. هناك {{count}} أمر يستحق النظر في طريقة استخدام خطوات الوكيل للبيانات (الفحوصات نفسها في لوحة الفحوصات في المحرر المتقدم):",
      changedAfterSubmit: "غيّرت إجابات بعد حفظ الإصدار {{version}}. لا يمكن تغيير إصدار محفوظ، لذلك تصبح تغييراتك الإصدار {{next}}.",
      bump: "إنشاء نسخة جديدة",
      saveDraft: "احفظه كمسودة",
      submit: "تقديم للإطلاق",
      submitConfirm: "هل تحفظ الإصدار {{version}}؟ بعد ذلك لا يمكن تغييره، وأي تعديل لاحق يصبح الإصدار التالي.",
      submitHint: "الحفظ يجمّد الإصدار {{version}} ليصبح قابلًا للإطلاق. يمكن تغيير المسودة لكن لا يمكن إطلاقها.",
      stagesTitle: "مراحل الإطلاق",
      stage: {
        draft: { title: "مسودة", body: "أنت فقط من يراها ويجرّبها." },
        dev: { title: "التطوير", body: "يمكن للمنشئين تجربته في المساعد، دون حاجة إلى اعتماد." },
        test: { title: "الاختبار", body: "يعتمده معتمِد إطلاق غيرك." },
        live: { title: "الإطلاق", body: "يتطلب اجتياز فحوص الجودة واعتماد معتمِد إطلاق." },
      },
      next: { save: "احفظ هذا الإصدار لبدء الإطلاق.", submit: "احفظ المسودة كإصدار لإطلاقها في التطوير.", saveChanges: "احفظ تغييراتك كالإصدار {{version}} لإطلاقها." },
      waiting: "بانتظار {{stage}}",
      waitingEvals: "يجب أن تنجح فحوص الجودة أولًا.",
      waitingSignOff: "يعتمده معتمِد إطلاق غيرك من الحوكمة › الإصدارات. لا يستطيع المنشئون اعتماد إطلاقاتهم بأنفسهم.",
      toDev: "الإطلاق في التطوير",
      toDevHint: "يتم فورًا، دون حاجة إلى اعتماد.",
      toTest: "طلب الاختبار",
      toTestHint: "سيُطلب من معتمِد إطلاق غيرك اعتماده.",
      liveHow: "يُطلب الفحص النهائي والتشغيل الفعلي من الحوكمة › الإصدارات بعد نجاح فحوص الجودة (الاختبارات المرجعية واختبارات الاختراق).",
      isLive: "هذه النسخة مُطلقة.",
      rejected: "رُفض آخر طلب إطلاق. راجع السبب في الحوكمة › الإصدارات.",
      promotionFailed: "فشل طلب الإطلاق",
      successTitle: "{{ref}} محفوظ",
      successBody: "النسخة مجمّدة وجاهزة للإطلاق. بعد وصولها إلى التطوير يمكنك استخدامها في المساعد.",
      openAssistant: "افتح في المساعد",
      openGovernance: "اعرض صفحته في الحوكمة",
      another: "إنشاء وكيل آخر",
      reason: "طُلب من المنشئ الموجَّه",
      promoted: "أصبح {{ref}} في {{stage}}",
      requested: "طُلب {{stage}} للوكيل {{ref}}",
      draftSaved: "حُفظت المسودة: {{ref}}",
      submitted: "قُدِّم: {{ref}}",
      unchanged: "{{ref}} مقدَّم مسبقًا بهذه الإجابات",
      useVersion: "استخدم الإصدار {{version}}",
      error: {
        "409": "هذه النسخة مقدَّمة ولا يمكن تغييرها",
        "422": "لم تقبل المنصة هذا الوكيل",
        "403": "لا تملك صلاحية حفظ الوكلاء",
        other: "تعذّر حفظ الوكيل",
      },
    },
    summary: {
      team_supervisor: "يسأل المشرف: {{members}}",
      team_handoff: "يسلّم المحادثة بين: {{members}}",
      autonomous: "يعمل وحده عندما يبدأه جدوله أو حدثه",
      title: "ما سيفعله وكيلك",
      unnamed: "وكيلك الجديد",
      purposeMissing: "أخبرنا في الخطوة 1 بما يجب أن يفعله.",
      someone: "شخص ما",
      tool: "{{tool}}: {{actions}}",
      approval: {
        never: "يغيّر البيانات دون أن يسأل أحدًا أولًا.",
        readOnly: "يبحث عن المعلومات فقط؛ ولا يغيّر شيئًا أبدًا.",
        before_changes: "يسأل {{roles}} قبل أن يغيّر أي شيء.",
        before_changes_amount: "يسأل {{roles}} قبل التغييرات التي تتجاوز {{amount}}.",
        before_changes_amount_some: "يسأل {{roles}} قبل أي تغيير؛ والمدفوعات فقط إذا تجاوزت {{amount}}.",
        always: "يراجع {{roles}} كل إجابة وكل تغيير.",
      },
      languages: "يتحدث {{langs}}",
      audience: "يستخدمه {{audience}}",
      groups: "مقتصر على المجموعات {{groups}}",
      topics: "يرسل الطلبات إلى {{topics}}",
      knowledge: "يبحث في {{list}}",
      nothing: "لا يستخدم مستندات أو أنظمة: يجيب مما يعرفه نموذج الذكاء الاصطناعي.",
      data: "يتعامل مع بيانات حتى مستوى {{level}}",
      model: "يستخدم نموذج الذكاء الاصطناعي {{model}}",
      ready: "جاهز للحفظ",
      stepProblem: "الخطوة {{n}}: {{text}}",
      technicalName: "الاسم التقني والإصدار",
    },
    problem: {
      team_members: "أضف وكيلين على الأقل إلى الفريق.",
      team_too_many: "يمكن أن يضم الفريق 8 أعضاء على الأكثر.",
      team_description: "اذكر ما يجيده {{member}}، ليعرف الفريق متى يسأله.",
      team_role: "أعطِ {{member}} اسمًا في الفريق.",
      team_limit: "يجب أن يكون الحد بين 1 و50.",
      trigger_unavailable: "طريقة البدء هذه لا تشغّل الوكيل بعد (الملفات الجديدة ورسائل قوائم الانتظار قادمة لاحقًا). اختر جدولًا زمنيًا أو بريدًا إلكترونيًا أو استدعاءً من نظام آخر.",
      autonomous_unchecked: "يعمل وحده ويغيّر البيانات دون أن يراجعه أحد. اختر «قبل أن يغيّر أي شيء» أو «دائمًا»، أو أوقف العمل وحده في الخطوة 1.",
      title_en: "أعطه اسمًا بالإنجليزية.",
      title_ar: "أعطه اسمًا بالعربية.",
      purpose_en: "اذكر بالإنجليزية ماذا يفعل.",
      purpose_ar: "اذكر بالعربية ماذا يفعل.",
      name: "اختر اسمًا تقنيًا: أحرف إنجليزية صغيرة وأرقام وشرطات، يبدأ بحرف، من 3 إلى 41 حرفًا.",
      version: "يجب أن يكون رقم النسخة بالشكل 1.0.0.",
      topics: "أضف فريقًا أو موضوعًا واحدًا على الأقل.",
      topic_empty: "الفريق {{n}} يحتاج إلى اسم.",
      sources: "اختر مجموعة معرفة أو نظامًا واحدًا على الأقل يجيب منه.",
      change_needed: "اختر أمرًا واحدًا على الأقل يغيّر البيانات: نقطة البداية هذه مخصصة للطلبات التي تحتاج إلى موافقة.",
      tool_missing: "{{tool}} غير متاح هنا. أزِله.",
      tool_uncertified: "{{tool}} غير معتمد لمنشئي الأعمال.",
      tool_no_actions: "حدّد ما يُسمح للوكيل بفعله في {{tool}}، أو أوقفه.",
      approver: "اختر من يوافق.",
      approval_needed: "نقطة البداية هذه تحتاج إلى موافقة شخص على التغييرات. اختر «قبل أن يغيّر أي شيء» أو «دائمًا».",
      amount: "يجب أن يكون المبلغ أكبر من 0، أو اتركه فارغًا.",
      collection_missing: "المجموعة {{collection}} غير متاحة هنا.",
      collection_level: "تحتوي {{collection}} بيانات {{level}}. اختر {{level}} أو مستوى أشد.",
      model: "اختر نموذج ذكاء اصطناعي.",
      model_missing: "نموذج الذكاء الاصطناعي {{model}} غير متاح هنا. اختر نموذجًا آخر.",
      model_level: "يستطيع {{model}} التعامل مع بيانات حتى مستوى {{level}}. اختر مستوى أقل أو نموذجًا آخر.",
      tool_level: "يحتوي {{tool}} بيانات {{level}}، لذا يُرفض الوكيل الذي مستواه أقل من {{level}} عند استخدامه. اختر {{level}} أو مستوى أشد.",
      action_tier: "«{{action}}» في {{tool}} إجراء عالي الخطورة (مستوى الخطورة {{tier}}). الوكلاء المصنوعون هنا يصلون إلى المستوى 2: ألغِ تحديده، أو اطلب من مطوّر بناء هذا الوكيل في المحرر المتقدم.",
      model_online: "{{model}} خدمة عبر الإنترنت، والبيانات السرية أو المقيّدة لا تُرسَل أبدًا إلى خدمات عبر الإنترنت. اختر نموذجًا يعمل على خوادمك.",
      technical: "فشل فحص تقني: {{detail}}",
      tools_needed: "فعّل نظامًا واحدًا على الأقل يمكنه جلب المعلومات منه.",
      knowledge_needed: "اختر مجموعة المعرفة التي تضم القواعد المراد المراجعة مقابلها.",
      reads_only: "هذا الوكيل يبحث عن المعلومات فقط. ألغِ تحديد الإجراءات التي تغيّر البيانات، أو ابدأ من «معالجة طلب يحتاج إلى موافقة».",
      schedule: "اختر متى يعمل: وفق جدول أو عند حدوث أمر ما.",
      schedule_incomplete: "أكمل الإجابة عن «متى يعمل؟»: ما زال شيء ناقصًا.",
    },
    handoff: {
      group: "من المسار الموجَّه",
      option: "مسودتك ({{ref}})",
      fromWizard: "هذا هو الوكيل من المسار الموجَّه، معروضًا كمخطط.",
      separate: "التعديلات التي تجريها هنا تبقى في المحرر المتقدم، وبعدها يُفتح الوكيل هنا بدلًا من المسار الموجَّه.",
      back: "العودة إلى المسار الموجَّه",
    },
  },
};
